"use client";

import ProfilePictureUpload from "../patient/profile-picture-upload";
import { InfiniteData, useQuery } from "@tanstack/react-query";
import { useContext, useMemo, useState } from "react";
import { FaPhoneAlt } from "react-icons/fa";
import { HiMinus, HiX } from "react-icons/hi";
import { HiFunnel, HiOutlineFunnel } from "react-icons/hi2";
import { MdMerge, MdOutlineBlock, MdOutlineMarkEmailRead, MdOutlineMarkEmailUnread, MdSwapHoriz } from "react-icons/md";
import { RiMore2Fill } from "react-icons/ri";
import { TbStar, TbStarFilled } from "react-icons/tb";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import CallModal from "@/components/patient/call-modal";
import PatientDisplay from "@/components/patient/patient-display";
import { ProgressBarLink } from "@/components/progress-bar";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSearch, DropdownMenuSeparator, DropdownMenuTrigger, DropdownSettingsButton } from "@/components/ui/dropdown";
import { FilterPill } from "@/components/ui/filter-pill";
import { queryClient } from "@/config/react-query";
import { useCommunication } from "@/contexts/chat-provider";
import { formatName, formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ChatSummary, isOptedOutOfSms, isSmsBlocked } from "@/types/chat";
import { UserType } from "@/types/enums";
import { ChatMode, ChatStatus } from "@/types/enums/chat";
import { CursorPage } from "@/types/pagination";
import { PatientSummary } from "@/types/patient";
import { CHANNEL_FILTER_LABELS, getChatChannel } from "./chat-filter";
import { CHAT_CHANNELS, type ChatChannel, ChatConfigContext, useChatFilterContext } from "./contexts";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { chatQueryKeys, chatQueryOptions, useOptOutFromTextingMutation, useUpdateChatModeMutation, useUpdateChatStatusMutation, useUpdatePrimaryPatientMutation } from "@/query/comms/chat";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
interface ChatHeaderProps {
  chatId?: string;
  size?: "sm" | "lg";
  onMinimize?: () => void;
  onClose?: () => void;
}
export function ChatHeader({
  chatId: propChatId,
  size: propSize,
  onMinimize: propOnMinimize,
  onClose: propOnClose
}: ChatHeaderProps) {
  // Try to get values from context first, fall back to props
  const configContext = useContext(ChatConfigContext);
  const chatId = propChatId ?? configContext?.chatId;
  const size = propSize ?? configContext?.size ?? "lg";
  const onMinimize = propOnMinimize ?? configContext?.onMinimize;
  const onClose = propOnClose ?? configContext?.onClose;
  const openSidebar = useRightSidebarStore(state => state.open);
  const {
    channels,
    toggleChannel,
    clearChannels,
    search,
    setSearch,
    searchQuery,
    isFiltered,
    clearFilters
  } = useChatFilterContext();
  const FilterIcon = isFiltered ? HiFunnel : HiOutlineFunnel;

  // What the pill under the header says, e.g. Calls only, or "botox" · Texts
  const channelLabels = CHAT_CHANNELS.filter(channel => channels.has(channel)).map(channel_0 => CHANNEL_FILTER_LABELS[channel_0]);
  const channelText = channelLabels.length === 1 && !searchQuery ? `${channelLabels[0]} only` : channelLabels.join(", ");
  const filterSummary = [searchQuery && `"${searchQuery}"`, channelText].filter(Boolean).join(" · ");
  const {
    activeCall
  } = useCommunication();
  const updateChatStatusMutation = useUpdateChatStatusMutation();
  const updateChatModeMutation = useUpdateChatModeMutation();
  const updatePrimaryPatientMutation = useUpdatePrimaryPatientMutation();
  const optOutFromTextingMutation = useOptOutFromTextingMutation();
  const {
    data: chatDetail,
    isPending: isChatDetailLoading
  } = useQuery({
    ...chatQueryOptions.detail(chatId ?? ""),
    enabled: !!chatId
  });
  const chatSummary = useMemo(() => {
    if (chatDetail) return null;
    const key = chatQueryKeys.infiniteList();
    const chatListQueries = queryClient.getQueriesData<InfiniteData<CursorPage<ChatSummary>>>({
      queryKey: key
    });
    const chatList = chatListQueries.find(([, data]) => {
      return data?.pages.some(page => page.data.some(chat => chat.id === chatId));
    });
    const data_0 = chatList?.[1];
    const matchingChat = data_0?.pages.flatMap(page_0 => page_0.data).find(chat_0 => chat_0.id === chatId);
    return matchingChat ?? null;
  }, [chatId, chatDetail]);
  const chat_1 = chatDetail ?? chatSummary;
  const channelCounts = useMemo(() => {
    const counts: Record<ChatChannel, number> = {
      sms: 0,
      patient_portal: 0,
      web_chat: 0,
      call: 0,
      email: 0
    };
    for (const item of [...(chatDetail?.messages ?? []), ...(chatDetail?.emails ?? [])]) {
      counts[getChatChannel(item)]++;
    }
    return counts;
  }, [chatDetail]);
  const isLoading = isChatDetailLoading && !chatSummary;
  const isUnread = chat_1?.status === ChatStatus.UNREAD;
  const primaryPatient = chat_1?.patients.find(p => p.id === chat_1?.primaryPatientId) || chat_1?.patients[0];
  const additionalPatients = chat_1?.patients.filter(p_0 => p_0.id !== primaryPatient?.id) || [];
  // A merge needs two live records. /user/patient/list drops archived patients and
  // merge_patient refuses them, so a link that includes one just dead-ends. Primary
  // first, since the merge screen treats the first id as the record to keep.
  const mergeablePatientIds = [...(primaryPatient && !primaryPatient.isArchived ? [primaryPatient] : []), ...additionalPatients.filter(p_1 => !p_1.isArchived)].map(p_2 => p_2.id);
  const handleSetPrimaryPatient = (e: React.MouseEvent, patientId: string) => {
    if (!chatId) return;
    e.preventDefault();
    e.stopPropagation();
    updatePrimaryPatientMutation.mutate({
      chatId: chatId,
      patientId
    });
  };

  // Shared button styling based on status
  const buttonClassName = cx("p-2", isUnread ? "text-background hover:bg-muted hover:text-foreground-secondary" : "text-foreground hover:text-foreground-secondary");

  // Shared text color classes
  const textColorClass = isUnread ? "text-background" : "text-foreground";

  // Shared dropdown menu content
  const renderDropdownContent = (handleCallPatient: () => void) => <DropdownMenuContent align={"center"}>
      {size === "lg" && <DropdownMenuItem disabled={isLoading} onSelect={e_0 => {
      if (!primaryPatient) return;
      e_0.preventDefault();
      openSidebar(patientSidebarDefinition, {
        patientTiny: {
          ...primaryPatient,
          type: UserType.PATIENT
        }
      });
    }}>
          View {formatName(primaryPatient?.firstName ?? "", undefined)}
          &apos;s Page
        </DropdownMenuItem>}
      <DropdownMenuItem onClick={() => {
      if (chat_1?.id) {
        const newStatus = chat_1.status === ChatStatus.READ ? ChatStatus.UNREAD : ChatStatus.READ;
        updateChatStatusMutation.mutate({
          chatId: chat_1.id,
          status: newStatus
        });
      }
    }} icon={chat_1?.status === ChatStatus.READ ? <MdOutlineMarkEmailUnread /> : <MdOutlineMarkEmailRead />} disabled={isLoading}>
        {chat_1?.status === ChatStatus.READ ? "Mark as Unresolved" : "Mark as Resolved"}
      </DropdownMenuItem>
      {/* A number that can't receive texts is pinned to the portal, as in the composer. */}
      {!(chat_1 && isSmsBlocked(chat_1)) && <DropdownMenuItem icon={<MdSwapHoriz />} disabled={isLoading} onClick={() => {
      if (!chat_1?.id) return;
      const nextMode = chat_1.mode === ChatMode.PATIENT_PORTAL ? ChatMode.SMS : ChatMode.PATIENT_PORTAL;
      updateChatModeMutation.mutate({
        chatId: chat_1.id,
        mode: nextMode
      });
    }}>
          {chat_1?.mode === ChatMode.PATIENT_PORTAL ? "Switch to Text" : "Switch to Patient Portal"}
        </DropdownMenuItem>}
      {size === "sm" && <DropdownMenuItem icon={<FaPhoneAlt />} onClick={handleCallPatient} disabled={isLoading}>
          Call Patient
        </DropdownMenuItem>}
      {chat_1 && mergeablePatientIds.length >= 2 && <DropdownMenuItem icon={<MdMerge />}>
          <ProgressBarLink href={`/patient/merge?patientIds=${mergeablePatientIds.join(",")}`} className="flex w-full items-center">
            Merge Patients
          </ProgressBarLink>
        </DropdownMenuItem>}
      {chat_1 && !isOptedOutOfSms(chat_1) && <DeleteConfirmationModal onConfirm={() => {
      if (!chat_1.id) return;
      optOutFromTextingMutation.mutate({
        chatId: chat_1.id
      });
    }} title={"Opt out of texting"} subtitle={size === "sm" ? `Are you sure you want to opt the phone number ${formatPhoneNumber(chat_1.phoneNumber ?? undefined)} out of texting? This action cannot be undone.` : `Prevent clinic from sending SMS messages to ${formatPhoneNumber(chat_1.phoneNumber ?? undefined)}. You will still receive any messages from the patient. Are you sure you want to opt this number out of texting?`}>
          <DropdownMenuItem className="text-status-error-text" onSelect={e_1 => e_1.preventDefault()} icon={<MdOutlineBlock className="text-status-error-text" />}>
            Opt out of texting
          </DropdownMenuItem>
        </DeleteConfirmationModal>}
    </DropdownMenuContent>;
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  // The widget sits above the page's pointer lock, so a second menu trigger
  // stays clickable; keep one menu open at a time.
  const [openMenu, setOpenMenu] = useState<"filter" | "actions" | null>(null);
  const menuState = (menu: "filter" | "actions") => ({
    open: openMenu === menu,
    onOpenChange: (open: boolean) => setOpenMenu(current => open ? menu : current === menu ? null : current)
  });
  const handleCallPatient_0 = () => {
    setIsCallModalOpen(true);
  };

  // Shared action buttons. Plain JSX rather than a nested component, so the
  // menus stay mounted (and open) when the header re-renders.
  const actionButtons = <>
      <div className={cx(size === "sm" ? "flex flex-shrink-0 items-center" : "flex items-center gap-2")}>
        <div className={cx("flex items-center", size === "lg" && "gap-2")}>
          {chat_1?.id && <DropdownMenu {...menuState("filter")}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" aria-label={isFiltered ? "Filter messages (on)" : "Filter messages"} className={cx(buttonClassName, isFiltered && (isUnread ? "bg-background/20" : "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"))}>
                  <FilterIcon className={cx(isUnread && "text-background", "h-4 w-4")} aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuSearch value={search} onChange={setSearch} placeholder="Search messages" />
                <DropdownMenuCheckboxItem checked={channels.size === 0} onCheckedChange={clearChannels} onSelect={e_2 => e_2.preventDefault()} hint={String((chatDetail?.messages.length ?? 0) + (chatDetail?.emails.length ?? 0))}>
                  All
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                {CHAT_CHANNELS.map(channel_1 => <DropdownMenuCheckboxItem key={channel_1} checked={channels.has(channel_1)} onCheckedChange={() => toggleChannel(channel_1)} onSelect={e_3 => e_3.preventDefault()} hint={String(channelCounts[channel_1])}>
                    {CHANNEL_FILTER_LABELS[channel_1]}
                  </DropdownMenuCheckboxItem>)}
              </DropdownMenuContent>
            </DropdownMenu>}
          {chat_1?.id && <DropdownMenu {...menuState("actions")}>
              <DropdownMenuTrigger asChild>
                {size === "sm" ? <Button variant="ghost" className={buttonClassName}>
                    <RiMore2Fill className={cx(isUnread && "text-background", "size-4 shrink-0")} aria-hidden="true" />
                  </Button> : <DropdownSettingsButton className={cx(isUnread && "text-background", "static translate-x-0 translate-y-0")} />}
              </DropdownMenuTrigger>
              {renderDropdownContent(handleCallPatient_0)}
            </DropdownMenu>}
        </div>
        {size === "lg" && primaryPatient && chat_1 && <CallModal patient={{
        ...primaryPatient,
        phoneNumber: chat_1.phoneNumber ?? undefined
      }}>
            <Button variant="ghost" aria-label="Call patient" className={cx(size === "lg" && buttonClassName)} disabled={!!activeCall || isLoading} icon={FaPhoneAlt} />
          </CallModal>}
        {size === "sm" && (onMinimize || onClose) && <>
            <div className={cx("mx-2 h-4 w-px", isUnread ? "bg-background/40" : "bg-border")} aria-hidden />
            <div className="flex items-center">
              {onMinimize && <Button variant="ghost" className={buttonClassName} onClick={onMinimize}>
                  <HiMinus className={cx(isUnread && "text-background", "h-4 w-4")} />
                </Button>}
              {onClose && <Button variant="ghost" className={buttonClassName} onClick={onClose}>
                  <HiX className={cx(isUnread && "text-background", "h-4 w-4")} />
                </Button>}
            </div>
          </>}
        {primaryPatient && chat_1 && <CallModal patient={{
        ...primaryPatient,
        phoneNumber: chat_1.phoneNumber ?? undefined
      }} open={isCallModalOpen} setOpen={setIsCallModalOpen} />}
      </div>
    </>;
  return <div className={cx("absolute left-0 right-0 top-0 z-10 flex flex-col items-start bg-gradient-to-b from-background via-background/70 to-transparent", size === "lg" ? "px-8 pt-4" : "rounded-t-2xl")}>
      {size === "sm" && <div className={cx("pointer-events-none absolute inset-0 z-10 shadow-inner")} />}

      <div className={cx("flex items-center justify-between p-2 px-2 backdrop-blur-sm", "shadow-md", isUnread ? "bg-primary/90" : "bg-background/80", size === "lg" ? "rounded-3xl md:w-1/2 md:min-w-96" : "w-full rounded-t-2xl")}>
        <div className={cx("ml-0 mr-2 min-w-0 flex-1 overflow-hidden", textColorClass)}>
          <div className="ml-2 flex w-full min-w-0 items-center gap-2 overflow-hidden">
            {isUnread && <span className="flex h-2 w-2 flex-shrink-0 rounded-full bg-status-error-text" />}
            {size === "lg" && primaryPatient && <ProfilePictureUpload patient={primaryPatient} onUploadSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: chatQueryKeys.detail(chatId)
            });
          }} />}
            <div>
              {isLoading ? <div className="h-6 w-24 animate-pulse rounded-full bg-muted" /> : <div className="flex items-center">
                  {primaryPatient && <PatientDisplay patient={primaryPatient as PatientSummary} nameClassName={cx(isUnread && "text-background", "font-bold")} />}

                  {additionalPatients.length > 0 && (primaryPatient?.id === chat_1?.primaryPatientId ? <TbStarFilled className="ml-1 text-yellow-500" /> : <button onClick={e_4 => handleSetPrimaryPatient(e_4, primaryPatient?.id ?? "")} className="ml-1 text-foreground-muted hover:text-yellow-500">
                        <TbStar />
                      </button>)}
                </div>}
              <div className="flex items-center gap-1">
                <p className={cx(isUnread && "text-background/80", "text-caption")}>
                  {formatPhoneNumber(chat_1?.phoneNumber ?? "")}
                </p>
                {chat_1 && !!additionalPatients.length && <Popover>
                    <PopoverTrigger className={cx(isUnread && "text-background/80", "text-caption")}>
                      +{additionalPatients.length} other patient
                      <span>{additionalPatients.length > 1 ? "s" : ""}</span>
                    </PopoverTrigger>
                    <PopoverContent align="start" sideOffset={5}>
                      {additionalPatients.slice(0, 4).map(patient => <div className="flex items-center" key={patient.id}>
                          <div className="group flex cursor-pointer items-center" onClick={() => openSidebar(patientSidebarDefinition, {
                      patientTiny: {
                        ...patient,
                        type: UserType.PATIENT
                      }
                    })}>
                            <PatientDisplay patient={patient as any} nameClassName="ml-1 text-h3 group-hover:text-primary" />
                          </div>
                          {patient.id === chat_1.primaryPatientId && <TbStarFilled className="ml-1 text-yellow-500" />}
                          {patient.id !== chat_1.primaryPatientId && <button onClick={e_5 => handleSetPrimaryPatient(e_5, patient.id)} className="ml-1 text-foreground-muted hover:text-yellow-500">
                              <TbStar />
                            </button>}
                        </div>)}
                    </PopoverContent>
                  </Popover>}
              </div>
            </div>
          </div>
        </div>
        {actionButtons}
      </div>
      {filterSummary && <FilterPill className="ml-2 mt-1.5 max-w-64 gap-1 bg-background/90 pr-1 text-foreground shadow-sm backdrop-blur-sm">
          <span className="truncate">{filterSummary}</span>
          <Button variant="ghost" icon={HiX} aria-label="Show all messages" onClick={clearFilters} className="!p-0" />
        </FilterPill>}
    </div>;
}

// Keep default export for backwards compatibility during migration
export default ChatHeader;